import firebase from 'firebase/compat/app';
import moment from 'moment-timezone';
import * as CryptoJS from 'crypto-js'

export interface Providers {
  provider: string
  providerName: string
  email: string
  password: string
  port: number
  server: string
  default: boolean
}

export type ProviderType = 'gmail' | 'sendgrid' | 'yahoo mail' | 'outlook' | 'other' | 'mailgun'

export interface SMTPServiceData {
  id?: string;
  company_id?: string;
  location_id?: string;
  date_added?: Date;
  date_updated?: Date;
  deleted?: boolean;
  provider_type?: ProviderType;
  provider_name?: string;
  email?: string;
  username?: string;
  password?: string;
  port?: number;
  server?: string;
  smtp_service_expired?: boolean;
  smtp_service_error?: string;
  enable_reply_tracking?: boolean;
  [key: string]: any;
}

// Collection reference helper
const collectionRef = (): firebase.firestore.CollectionReference => {
  return firebase.firestore().collection('smtp_service');
};

// Create a service
export const createSMTPService = (snapshot?: firebase.firestore.DocumentSnapshot | SMTPServiceData): {
  // Properties
  ref: firebase.firestore.DocumentReference;
  id: string;
  data: SMTPServiceData;
  
  // Getters/Setters - implemented as functions for compatibility
  getCompanyId: () => string;
  setCompanyId: (companyId: string) => void;
  getLocationId: () => string;
  setLocationId: (locationId: string) => void;
  getDateAdded: () => moment.Moment;
  setDateAdded: (dateAdded: moment.Moment) => void;
  getDateUpdated: () => moment.Moment;
  setDateUpdated: (dateUpdated: moment.Moment) => void;
  getDeleted: () => boolean;
  setDeleted: (deleted: boolean) => void;
  getProviderType: () => ProviderType;
  setProviderType: (providerType: ProviderType) => void;
  getProviderName: () => string;
  setProviderName: (providerName: string) => void;
  getEmail: () => string;
  setEmail: (email: string) => void;
  getUsername: () => string;
  setUsername: (username: string) => void;
  getPassword: () => string;
  setPassword: (password: string) => void;
  getPort: () => number;
  setPort: (port: number) => void;
  getServer: () => string;
  setServer: (server: string) => void;
  getSmtpServiceExpired: () => boolean;
  setSmtpServiceExpired: (smtpServiceExpired: boolean) => void;
  getSmtpServiceError: () => string;
  setSmtpServiceError: (smtpServiceError: string) => void;
  getEnableReplyTracking: () => boolean;
  setEnableReplyTracking: (enableReplyTracking: boolean) => void;
  
  // Methods
  save: () => Promise<any>;
} => {
  let _id: string;
  let _data: SMTPServiceData = {};
  let _ref: firebase.firestore.DocumentReference;

  if (snapshot instanceof firebase.firestore.DocumentSnapshot) {
    _id = snapshot.id;
    _ref = snapshot.ref;
    _data = snapshot.data() as SMTPServiceData || {};
  } else if (snapshot) {
    _id = snapshot.id || '';
    _ref = collectionRef().doc(snapshot.id || '');
    _data = snapshot || {};
  } else {
    _ref = collectionRef().doc();
    _id = _ref.id;
    _data = {};
    _data.date_added = moment().toDate();
    _data.enable_reply_tracking = true;
    _data.deleted = false;
  }

  return {
    // Properties
    ref: _ref,
    id: _id,
    data: _data,
    
    // Getters/Setters
    getCompanyId: () => _data.company_id as string,
    setCompanyId: (companyId: string) => { _data.company_id = companyId },
    
    getLocationId: () => _data.location_id as string,
    setLocationId: (locationId: string) => { _data.location_id = locationId },
    
    getDateAdded: () => moment(_data.date_added),
    setDateAdded: (dateAdded: moment.Moment) => { _data.date_added = dateAdded.toDate() },
    
    getDateUpdated: () => moment(_data.date_updated),
    setDateUpdated: (dateUpdated: moment.Moment) => { _data.date_updated = dateUpdated.toDate() },
    
    getDeleted: () => !!_data.deleted,
    setDeleted: (deleted: boolean) => { _data.deleted = deleted },
    
    getProviderType: () => _data.provider_type as ProviderType,
    setProviderType: (providerType: ProviderType) => { _data.provider_type = providerType },
    
    getProviderName: () => _data.provider_name as string,
    setProviderName: (providerName: string) => { _data.provider_name = providerName },
    
    getEmail: () => _data.email as string,
    setEmail: (email: string) => { _data.email = email },
    
    getUsername: () => _data.username as string,
    setUsername: (username: string) => { _data.username = username },
    
    getPassword: () => {
      if (_data.password)
        return CryptoJS.AES.decrypt(_data.password, 'apples&oranges').toString(CryptoJS.enc.Utf8)
      return '';
    },
    setPassword: (password: string) => { 
      _data.password = CryptoJS.AES.encrypt(password, 'apples&oranges').toString() 
    },
    
    getPort: () => _data.port as number,
    setPort: (port: number) => { _data.port = port },
    
    getServer: () => _data.server as string,
    setServer: (server: string) => { _data.server = server },
    
    getSmtpServiceExpired: () => _data.smtp_service_expired !== undefined ? !!_data.smtp_service_expired : false,
    setSmtpServiceExpired: (smtpServiceExpired: boolean) => { _data.smtp_service_expired = smtpServiceExpired },
    
    getSmtpServiceError: () => _data.smtp_service_error as string,
    setSmtpServiceError: (smtpServiceError: string) => { _data.smtp_service_error = smtpServiceError },
    
    getEnableReplyTracking: () => _data.enable_reply_tracking !== undefined ? !!_data.enable_reply_tracking : true,
    setEnableReplyTracking: (enableReplyTracking: boolean) => { _data.enable_reply_tracking = enableReplyTracking },
    
    // Methods
    save: async () => {
      try {
        _data.date_updated = moment().utc().toDate();
        await _ref.set(_data, { merge: true });
        return { id: _id, data: _data, ref: _ref };
      } catch (ex) {
        throw ex;
      }
    }
  };
};

// Static methods as standalone functions
export const getById = (id: string): Promise<ReturnType<typeof createSMTPService>> => {
  return new Promise((resolve, reject) => {
    collectionRef()
      .doc(id)
      .get()
      .then((snapshot) => {
        resolve(createSMTPService(snapshot));
      })
      .catch((err) => {
        console.error(err);
        reject(err);
      });
  });
};

export const getByLocationId = async (locationId: string): Promise<ReturnType<typeof createSMTPService>[]> => {
  return new Promise((resolve) => {
    collectionRef()
      .where('location_id', '==', locationId)
      .where('deleted', '==', false)
      .get()
      .then(snapshot => {
        if (snapshot.empty) return resolve([]);
        return resolve(snapshot.docs.map(d => createSMTPService(d)));
      })
      .catch(err => {
        console.error(err);
        return resolve([]);
      });
  });
};

export const getByEmailId = async (email: string): Promise<ReturnType<typeof createSMTPService>[]> => {
  return new Promise((resolve) => {
    collectionRef()
      .where('email', '==', email)
      .where('deleted', '==', false)
      .get()
      .then(snapshot => {
        if (snapshot.empty) return resolve([]);
        return resolve(snapshot.docs.map(d => createSMTPService(d)));
      })
      .catch(err => {
        console.error(err);
        return resolve([]);
      });
  });
};

export const fetchByLocationIdRealtime = (locationId: string): firebase.firestore.Query => {
  return collectionRef()
    .where('deleted', '==', false)
    .where('location_id', '==', locationId);
};

export const fetchByCompanyIdRealtime = (companyId: string): firebase.firestore.Query => {
  return collectionRef()
    .where('deleted', '==', false)
    .where('company_id', '==', companyId);
};

// For backward compatibility with code that uses "new SMTPService()"
export default class SMTPService {
  public static collectionRef = collectionRef;
  public static getById = getById;
  public static getByLocationId = getByLocationId;
  public static getByEmailId = getByEmailId;
  public static fetchByLocationIdRealtime = fetchByLocationIdRealtime;
  public static fetchByCompanyIdRealtime = fetchByCompanyIdRealtime;

  private _service: ReturnType<typeof createSMTPService>;

  constructor(snapshot?: firebase.firestore.DocumentSnapshot | SMTPServiceData) {
    this._service = createSMTPService(snapshot);
  }

  get ref() { return this._service.ref; }
  get id() { return this._service.id; }
  get data() { return this._service.data; }
  
  get companyId() { return this._service.getCompanyId(); }
  set companyId(value) { this._service.setCompanyId(value); }
  
  get locationId() { return this._service.getLocationId(); }
  set locationId(value) { this._service.setLocationId(value); }
  
  get dateAdded() { return this._service.getDateAdded(); }
  set dateAdded(value) { this._service.setDateAdded(value); }
  
  get dateUpdated() { return this._service.getDateUpdated(); }
  set dateUpdated(value) { this._service.setDateUpdated(value); }
  
  get deleted() { return this._service.getDeleted(); }
  set deleted(value) { this._service.setDeleted(value); }
  
  get providerType() { return this._service.getProviderType(); }
  set providerType(value) { this._service.setProviderType(value); }
  
  get providerName() { return this._service.getProviderName(); }
  set providerName(value) { this._service.setProviderName(value); }
  
  get email() { return this._service.getEmail(); }
  set email(value) { this._service.setEmail(value); }
  
  get username() { return this._service.getUsername(); }
  set username(value) { this._service.setUsername(value); }
  
  get password() { return this._service.getPassword(); }
  set password(value) { this._service.setPassword(value); }
  
  get port() { return this._service.getPort(); }
  set port(value) { this._service.setPort(value); }
  
  get server() { return this._service.getServer(); }
  set server(value) { this._service.setServer(value); }
  
  get smtpServiceExpired() { return this._service.getSmtpServiceExpired(); }
  set smtpServiceExpired(value) { this._service.setSmtpServiceExpired(value); }
  
  get smtpServiceError() { return this._service.getSmtpServiceError(); }
  set smtpServiceError(value) { this._service.setSmtpServiceError(value); }
  
  get enableReplyTracking() { return this._service.getEnableReplyTracking(); }
  set enableReplyTracking(value) { this._service.setEnableReplyTracking(value); }
  
  public async save() {
    return this._service.save();
  }
}
